import SearchHeader from '@components/common/SearchHeader';
import useUpdateEdgeDevices from '@hooks/useUpdateEdgeDevices';
import { Autocomplete, Box, Button, Dialog, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { IStoreState } from '@store/index';
import React from 'react';
import { useSelector } from 'react-redux';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import Utils from '@utils/index';
import { useMobileQuery } from '@hooks/useMobileQuery';
import { IEdgeDevice } from '@store/assets/types';
import { useSmallScreenQuery } from '@hooks/useSmallScreenQuery';
import { first, isEmpty, isObject, last } from 'lodash';
import { InfoOutlined, ShowChart } from '@mui/icons-material';
import Metrics from '@components/common/Metrics';
import CustomDataGrid from '@components/common/DataGrid/CustomDataGrid';
import NonIdealState from '@components/common/NonIdealState/NonIdealState';
import styled from 'styled-components';
import { useSnackbar } from 'notistack';
import LoadingContent from '@components/overview/LoadingContent';
import API from '@API/index';
import { useDispatch } from 'react-redux';
import { setDevices as ReduxSetDevices } from '@store/assets/actions';
import { setSites } from '@store/sites/actions';

const Container = styled(Box)`
  .MuiOutlinedInput-notchedOutline {
    height: unset;
  }

  .MuiInputBase-root {
    height: unset;
  }
`

const HealthEdgeDevices: React.FC<{}> = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const isMobile = useMobileQuery();
  const isSmallScreen = useSmallScreenQuery();
  const isSuperAdministrator = useSelector((store: IStoreState) => store.session.isSuperAdministrator);

  const reduxEdgeDevices = useSelector((store: IStoreState) => store.assets.assets);
  const selectedCompany = useSelector((store: IStoreState) => store.selectedCompanies.selectedCompany);
  const selectedSite = useSelector((store: IStoreState) => store.selectedSites.selectedSite);
  const companies = useSelector((store: IStoreState) => store.companies.companies);
  const sites = useSelector((store: IStoreState) => store.sites.sites);

  const [edgeDevices, setEdgeDevices] = React.useState<IEdgeDevice[]>(selectedSite ? reduxEdgeDevices.filter(el => el.siteId === selectedSite.siteId) : reduxEdgeDevices)
  const [filteredEdgeDevices, setFilteredEdgeDevices] = React.useState<IEdgeDevice[]>(edgeDevices);
  const [devices, setDevices] = React.useState<IEdgeDevice[]>([]);
  const [selectedDevices, setSelectedDevices] = React.useState<IEdgeDevice[]>([]);
  const [isAssignDialogOpen, setAssignDialogOpen] = React.useState<boolean>(false);
  const [companyId, setCompanyId] = React.useState<string | undefined>(undefined);
  const [siteId, setSiteId] = React.useState<string | undefined>(undefined);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [loadingEdgeDevices, setLoadingEdgeDevices] = React.useState<boolean>(false);

  const isUpdatingEdgeDevices = useUpdateEdgeDevices(selectedCompany?.companyId);

  console.log(edgeDevices);

  const availableCompanies = React.useMemo(() => {
    return companies.filter(el => el.companyId !== selectedCompany?.companyId);
  }, [companies, selectedCompany?.companyId]);

  const availableSites = React.useMemo(() => {
    return sites.get(companyId || "") || []
  }, [companyId, sites])

  const isAssingDeviceDisabled = React.useMemo(() => {
    return selectedDevices.length === 0;
  }, [selectedDevices.length])

  const handleAssignDeviceClick = React.useCallback(() => {
    setAssignDialogOpen(true);
  }, [])

  const confirm = React.useCallback(async () => {
    try {
      setLoading(true);
      const iotThingNames = selectedDevices.map((e) => e.iotThingName)
      if (companyId && siteId) {
        await Promise.all(iotThingNames.map((iotThingName) => API.EdgeDevices.changeEdgeDeviceSite(iotThingName, companyId, siteId)))
        setAssignDialogOpen(false);
        setLoading(false);
        enqueueSnackbar('Changes saved!', { variant: 'success' })
        setLoadingEdgeDevices(true)
        const edgeDevices = await API.EdgeDevices.listDevices('All', undefined, selectedCompany?.companyId)
        dispatch(ReduxSetDevices(edgeDevices))
        setSelectedDevices([])
        setLoadingEdgeDevices(false);
      } else {
        setAssignDialogOpen(false);
        setLoading(false);
        setLoadingEdgeDevices(false)
        enqueueSnackbar('Error while extracting companyId or siteId!', { variant: 'error' })
      }
    } catch (error) {
      enqueueSnackbar(Utils.Error.getErrorMessage(error), { variant: 'error' })
      setLoading(false);
    }
  }, [companyId, dispatch, enqueueSnackbar, selectedCompany?.companyId, selectedDevices, siteId])

  const closeDialog = React.useCallback(() => {
    setAssignDialogOpen(false);
  }, [])

  React.useEffect(() => {
    if (selectedSite) {
      setEdgeDevices(reduxEdgeDevices.filter(el => el.siteId === selectedSite.siteId))
    } else {
      setEdgeDevices(reduxEdgeDevices);
    }
  }, [selectedSite, selectedCompany, reduxEdgeDevices])

  React.useEffect(() => {
    Promise.all(companies.map((company) => API.Site.listSites(company.companyId)))
      .then((wholeSites: Site[][]) => {
        wholeSites.forEach((sites) => {
          const cId = first(sites)?.companyId;
          if (cId) {
            dispatch(setSites(cId, sites));
          }
        })
      })
  }, [companies, dispatch])

  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<GridColumnVisibilityModel>({
    'companyId': false,
    'siteId': true,
    'ioThingName': true,
    'mac': true,
    'statusInfo': true,
    'lastDeploymentInfo': true,
    'componentVersions': true
  })

  const changeVisibilityFromMobile = React.useCallback(() => {
    if (isMobile || isSmallScreen) {
      setColumnVisibilityModel({
        'companyId': false,
        'siteId': false,
        'ioThingName': true,
        'mac': true,
        'statusInfo': false,
        'lastDeploymentInfo': false,
        'componentVersions': false,
      })
    } else {
      setColumnVisibilityModel({
        'companyId': false,
        'siteId': true,
        'ioThingName': true,
        'mac': true,
        'statusInfo': true,
        'lastDeploymentInfo': true,
        'componentVersions': true
      })
    }
  }, [isMobile, isSmallScreen])

  React.useEffect(changeVisibilityFromMobile, [changeVisibilityFromMobile])
  const columns = React.useMemo(() => {
    const base = [
      {
        field: 'companyId',
        headerName: 'Company ID',
        flex: 0.25,
        sortable: true,
        renderCell: (params: any) => (
          <Typography noWrap variant="body2">{params.value}</Typography>
        )
      },
      {
        field: 'siteId',
        headerName: 'Site ID',
        flex: 0.25,
        sortable: true,
        renderCell: (params: any) => (
          <Typography noWrap variant="body2">{params.value}</Typography>
        )
      },
      {
        field: 'iotThingName',
        headerName: 'IOT thing name',
        flex: 0.25,
        sortable: true,
        renderCell: (params: any) => (
          <Typography noWrap variant="body2">{params.value}</Typography>
        )
      },
      {
        field: 'mac',
        headerName: 'Mac address',
        flex: 0.25,
        sortable: true,
        renderCell: (params: any) => (
          <Typography noWrap variant="body2">{params.value}</Typography>
        ),
      },
      {
        field: 'statusInfo',
        headerName: 'Status',
        flex: 0.25,
        sortable: true,
        renderCell: (params: any) => {
          if (params.value) {
            return (
              <Box>
                {params?.value?.status && <Typography noWrap variant="body2">{`${params.value.status}`}</Typography>}
                {params?.value?.timestamp && <Typography noWrap variant="body2">{`${Utils.Date.format(new Date(params.value.timestamp), 'MMM Do, YYYY h:00 A')} (UTC)`}</Typography>}
              </Box>
            )
          }

          return (
            <Typography noWrap variant="body2">{`-`}</Typography>
          )
        }
      },

      {
        field: 'lastDeploymentInfo',
        headerName: 'Last deployment',
        flex: 0.25,
        sortable: true,
        renderCell: (params: any) => {
          if (params.value) {
            return (
              <Box>
                {params?.value?.status && <Typography noWrap variant="body2">{`${params.value.status}`}</Typography>}
                {params?.value?.reason && <Typography noWrap variant="body2">{`${params.value.reason}`}</Typography>}
                {params?.value?.timestamp && <Typography noWrap variant="body2">{`${Utils.Date.format(new Date(params.value.timestamp), 'MMM Do, YYYY h:00 A')} (UTC)`}</Typography>}
              </Box>
            )
          }

          return (
            <Typography noWrap variant="body2">{`-`}</Typography>
          )
        }
      },

      {
        field: 'componentVersions',
        headerName: 'Component versions',
        flex: 0.25,
        sortable: true,
        renderCell: (params: any) => {
          if (params.value && isObject(params.value) && !isEmpty(params.value)) {
            const keys = Object.keys(params.value)
            const values = Object.values(params.value);
            console.log(params.value)

            return (
              <Box>
                <Tooltip
                  title={
                    <Box>
                      {keys.map((el, index) => (
                        <Typography noWrap variant="body2">{`${el}: ${values[index]}`}</Typography>
                      ))}
                    </Box>
                  }
                >
                  <IconButton style={{ padding: 0 }}><InfoOutlined /></IconButton>
                </Tooltip>
              </Box>
            )
          }
          return (
            <Typography noWrap variant="body2">{`-`}</Typography>
          )
        }
      }
    ]

    if (!isMobile && !isSmallScreen) {
      base.push({
        field: 'metrics',
        headerName: 'Metrics',
        flex: 0.10,
        sortable: false,
        renderCell: (params: any) => (
          <IconButton
            onClick={() => { setDevices([params.row]) }}
            size="medium"
          >
            <ShowChart />
          </IconButton>
        )
      })
    }

    return base;
  }, [isMobile, isSmallScreen])

  const textFilterProps = React.useMemo(() => ["siteId", "iotThingName", "mac"], []);

  const devicesToShow = React.useMemo(() => {
    if (isUpdatingEdgeDevices) return [];

    return Utils.Array.orderAlphabetically(filteredEdgeDevices, 'companyId').map((edgeDevice: IEdgeDevice) => ({
      id: `${edgeDevice.iotThingName}`,
      companyId: edgeDevice.companyId,
      siteId: edgeDevice.siteId,
      iotThingName: edgeDevice.iotThingName,
      mac: edgeDevice.mac,
      metrics: edgeDevice.iotThingName,
      statusInfo: edgeDevice.statusInfo,
      lastDeploymentInfo: edgeDevice.lastDeploymentInfo,
      componentVersions: edgeDevice.componentVersions

    }))
  }, [filteredEdgeDevices, isUpdatingEdgeDevices])

  if (isSuperAdministrator === false) {
    return (
      <NonIdealState
        title="Not found"
        description="Page was not found, please check your information."
        errorCode={404}
      />
    )
  }

  if (!isEmpty(devices)) {
    return (
      <Metrics
        devices={devices}
        onClose={() => setDevices([])}
      />
    )
  }

  return (
    <Box display="grid" gridTemplateRows="min-content auto" width="100%" height="100%" padding="1.5rem" gap="2rem">
      <SearchHeader
        title="Edge devices"
        textFilterProps={textFilterProps}
        textFiltering={true}
        noPadding
        searchHeaderFilterProps={{
          onFilteredDataChange: (data: any[]) => { setFilteredEdgeDevices(data) },
          data: edgeDevices,
          properties: [
            {
              label: 'Company ID',
              property: 'companyId',
              type: 'string'
            },
            {
              label: 'Site ID',
              property: 'siteId',
              type: 'string'
            },
            {
              label: 'IOT thing name',
              property: 'iotThingName',
              type: 'string'
            },
            {
              label: 'Mac address',
              property: 'mac',
              type: 'string'
            }
          ]
        }}
      >
        <Button
          variant="contained"
          color="primary"
          disabled={isAssingDeviceDisabled}
          onClick={handleAssignDeviceClick}
        >
          Assign devices
        </Button>
      </SearchHeader>
      <CustomDataGrid
        hideHeaders={isUpdatingEdgeDevices || filteredEdgeDevices.length === 0}
        loading={isUpdatingEdgeDevices || loadingEdgeDevices}
        defaultHeight='82vh'
        disableColumnFilter
        disableRowSelectionOnClick
        rows={devicesToShow}
        columns={columns}
        onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
        columnVisibilityModel={columnVisibilityModel}
        dataCy="overview-edge-devices-table"
        checkboxSelection
        onRowSelectionModelChange={(newSelectionModel) => {
          setSelectedDevices(edgeDevices.filter(el => newSelectionModel.includes(el.iotThingName)))
        }}
        rowSelectionModel={selectedDevices.map(e => e.iotThingName)}
        getRowClassName={(params) => {
          if (
            params?.row?.statusInfo?.status &&
            params?.row?.statusInfo?.status !== "online"
          ) {
            return `health-error`
          }

          if (
            params?.row?.statusInfo?.lastDeploymentInfo?.status &&
            params?.row?.statusInfo?.lastDeploymentInfo?.status !== "SUCCEEDED" &&
            params?.row?.statusInfo?.lastDeploymentInfo?.status !== "COMPLETED"
          ) {
            return `health-error`
          }

          return '';
        }}
      />
      <Dialog
        open={isAssignDialogOpen}
        onClose={closeDialog}
      >
        {loading === true &&
          <Box
            width={"60vw"}
            height={"85vh"}
            gap="1.5rem"
            padding="2rem"
          >
            <LoadingContent message="Sending information..." />
          </Box>
        }
        {loading === false &&
          <Container
            display="grid"
            width={"60vw"}
            height={"85vh"}
            gridTemplateRows="max-content max-content auto max-content"
            gap="1.5rem"
            padding="2rem"
          >
            <Typography variant="h5">Assign devices</Typography>
            <Autocomplete
              disablePortal
              options={availableCompanies.map(el => el)}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} label={<Typography variant="subtitle1" >Choose the company</Typography>} />}
              onChange={(_e, r) => setCompanyId(r?.companyId as string)}
              fullWidth
            />
            <Autocomplete
              disablePortal
              options={availableSites.map(el => el)}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} label={<Typography variant="subtitle1" >Choose the site</Typography>} />}
              disabled={companyId === undefined}
              onChange={(_e, r) => setSiteId(r?.name as string)}
              fullWidth
            />
            <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%">
              <Button
                onClick={closeDialog}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={companyId === undefined || siteId === undefined}
                onClick={confirm}
              >
                Confirm
              </Button>
            </Box>
          </Container>
        }
      </Dialog>
    </Box>
  )
}

export default HealthEdgeDevices;
